import { defineStore } from 'pinia';
import { useSprinklerStore } from '@/stores/SprinklerStore';

export const usePrintStore = defineStore('print', {
  state () {
    return {
      letterRatio: {
        long: 1.29411764706,
        short: 0.77272727272,
        base: 1260,
      }, // 0.77272727272,
      a4Ratio: {
        long: 1.414285714285714,
        short: 0.70707070707,
        base: 1400
      },
      size: 'letter', // a4 / letter
      orientation: 'landscape', // portrait / landscape
      printIsOpen: false,
      printDebug: 0,
      pageScale: 1,
      printInput: {
        notes: '',
        jobTitle: 'test'
      },
      printData: [
        // TODO: need to validate and replace incorrect values/references
        // TODO: *** means highly unsure
        {
          group: 1,
          data: [
            { label: 'LBL_PRODUCT', value: 'Sprinkler' },
            { label: 'LBL_MODEL', value: 'Model' },
            { label: 'LBL_PLATE', value: 'Plate' },
            { label: 'LBL_TRAJECTORY', value: 'Traj' },
            { label: 'LBL_NOZZLE', value: 'Nozzle' },
            { label: 'LBL_PRESSURE', value: 'Pressure', unit: 'pressure' }
          ]
        },
        {
          group: 2,
          data: [
            { label: 'LBL_FLOW', value: 'FlowRate', unit:'flow' },
            { label: 'LBL_RISER_HT', value: 'RiserHt', unit:'height' },
            { label: 'LBL_MINS_PER_REV', value: 'MinsPerRev', unit: 'mins' },
            { label: 'LBL_STREAM_HT', value: 'StreamHeight', unit:'height' },
            { label: 'LBL_WETTED_RADIUS', value: 'Radius', unit: 'length' },
            { label: 'LBL_WIND', value: 'WndSpd', unit: 'speed' }
          ]
        },
        {
          group: 3,
          data: [
            { label: 'LBL_SPACING', value: 'Spacing', unit: 'spacing' },
            { label: 'LBL_OFFSET', value: 'Offset', unit: 'length' },
            { label: 'LBL_LAYOUT', value: 'Layout' },
            { label: 'LBL_THEOR_PRECIP', value: 'TheoreticalPrecipRate', unit: 'precip' },
            { label: 'LBL_FLOW_PER_AREA', value: 'TheoreticalPrecipRatePerAcre', unit: 'rate' }, // ***
            { label: 'LBL_AREA_PER_SPRINKLER', value: 'AreaPerSprinkler', unit: 'area' }
          ]
        },
        {
          group: 4,
          data: [
            { label: 'LBL_CU', value:'CU', unit: '%' },
            { label: 'LBL_DU', value:'DU', unit: '%' },
            { label: 'LBL_SC', value:'SC', unit: '%' },
            { label: 'LBL_PERCENT_OVERLAP', value:'PcentOlap' },
            { label: 'LBL_MEAN_PRECIP', value:'AppRate', unit: 'precip' }, // ***
            { label: 'LBL_MIN_PRECIP', value:'MinRate', unit: 'precip' }, // ***
            { label: 'LBL_MAX_PRECIP', value:'MaxRate', unit: 'precip' } // ***
          ]
        },
      ],
      treeVinePrintData: [
        {
          group: 'strips',
          data: [
            { label: 'CU', value: 'StatsCU' },
            { label: 'DU', value: 'StatsDU' },
            { label: 'SC', value: 'StatsSC' },
            { label: 'Mean', value: 'StatsAppRate', unit: 'precip' },
            { label: 'Min', value: 'StatsMinRate', unit: 'precip' },
            { label: 'Max', value: 'StatsMaxRate', unit: 'precip' },
            { label: '% Water', value: 'PercentWaterStrip' },
          ]
        },
        {
          group: 'trees',
          data: [
            { label: 'CU', value: 'TreeStatsCU' },
            { label: 'DU', value: 'TreeStatsDU' },
            { label: 'SC', value: 'TreeStatsSC' },
            { label: 'Mean', value: 'TreeStatsAppRate', unit: 'precip' },
            { label: 'Min', value: 'TreeStatsMinRate', unit: 'precip' },
            { label: 'Max', value: 'TreeStatsMaxRate', unit: 'precip' },
            { label: '% Water', value: 'PercentWaterTree' },
          ]
        },
      ],
      printQueue: [],
      currentPrintPage: 0
    };
  },
  getters: {
    getPrintIsOpen () {
      return this.printIsOpen;
    },
    getRatio () {
      return this.size === 'letter' ? this.letterRatio : this.a4Ratio;
    },
    getSize () {
      return this.size;
    },
    getOrientation () {
      return this.orientation;
    },
    getPageScale () {
      return this.pageScale;
    },
    getJobTitle () {
      return this.printInput.jobTitle;
    },
    getJobNotes () {
      return this.printInput.notes;
    },
    getPrintData () {
      return this.printData;
    },
    getTreeVinePrintData () {
      return this.treeVinePrintData;
    },
    getPrintQueue () {
      return useSprinklerStore().records.filter((item) => { if (item.isMarked) { return item; } });
    },
    getCurrentPrintPage () {
      return this.currentPrintPage;
    },
    getTotalPrintPages () {
      return this.getPrintQueue.length;
    },
    getDimensions () {
      let sizeRatio = (this.size === 'letter') ? this.letterRatio : this.a4Ratio;
      return this.orientation === 'landscape' ? {
        width: sizeRatio.base,
        height: sizeRatio.base * sizeRatio.short
      } : {
        width: sizeRatio.base * sizeRatio.short,
        height: sizeRatio.base
      };
    }
  },
  actions: {
    setPrintIsOpen (bool) {
      this.printIsOpen = bool;
    },
    setSize (type) {
      this.size = type;
    },
    setOrientation (type) {
      this.orientation = type;
    },
    setPageScale (val) {
      this.pageScale = val;
    },
    setJobTitle (val) {
      this.printInput.jobTitle = val;
    },
    setJobNotes (val) {
      this.printInput.notes = val;
    },
    addToPrintQueue (record) {
      this.printQueue.push(record);
    },
    clearPrintQueue () {
      this.printQueue = [];
    },
    selectLastInPrintQueue () {
      this.printQueue = [this.printQueue.pop()];
    },
    showPrintPage (num) {
      this.currentPrintPage += num;
    }
  }
});